import React, { useEffect, useState, useCallback } from 'react';
import { featureAPI } from '../../../api/services';
import { Feature } from '../../../types';

interface Props {
  projectId: string;
  featureId?: string;
  onCreated?: (featureId: string) => void;
  onCancel?: () => void;
}

const FeatureForm: React.FC<Props> = ({ projectId, featureId, onCreated, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    weight: 10,
    visibility: 'public_org' as 'private' | 'public_org' | 'public',
    startDate: '',
    endDate: '',
    tags: '',
  });

  const isEdit = !!featureId;

  const loadFeature = useCallback(async () => {
    if (!featureId) return;
    try {
      setFetchLoading(true);
      const res = await featureAPI.getById(featureId);
      const f: Feature = res.data.data ?? res.data;
      setForm({
        name: f.name,
        description: f.description,
        priority: f.priority,
        weight: f.weight,
        visibility: f.visibility === 'custom' ? 'private' : f.visibility as any,
        startDate: f.dates?.startDate ? f.dates.startDate.split('T')[0] : '',
        endDate: f.dates?.estimatedEndDate ? f.dates.estimatedEndDate.split('T')[0] : '',
        tags: f.tags?.join(', ') || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load feature');
    } finally {
      setFetchLoading(false);
    }
  }, [featureId]);

  useEffect(() => {
    loadFeature();
  }, [loadFeature]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === 'weight' ? Number(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const tags = form.tags
      ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];

    const payload: any = {
      projectId,
      name: form.name,
      description: form.description,
      priority: form.priority,
      weight: form.weight,
      visibility: form.visibility,
      dates: {
        startDate: form.startDate || undefined,
        estimatedEndDate: form.endDate || undefined,
      },
      tags,
    };

    try {
      let res;
      if (isEdit) {
        res = await featureAPI.update(featureId!, payload);
      } else {
        res = await featureAPI.create(payload);
      }
      const feature = res.data.data ?? res.data;
      onCreated?.(feature._id);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save feature');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Feature' : 'New Feature'}</h2>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          placeholder="Feature name"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          required
          rows={4}
          placeholder="What does this feature deliver?"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            name="priority"
            value={form.priority}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Weight (0-100)</label>
          <input
            name="weight"
            type="number"
            min={0}
            max={100}
            value={form.weight}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
        <select
          name="visibility"
          value={form.visibility}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="private">Private (only me)</option>
          <option value="public_org">Organisation (default)</option>
          <option value="public">Public</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            name="startDate"
            type="date"
            value={form.startDate}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            name="endDate"
            type="date"
            value={form.endDate}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
        <input
          name="tags"
          value={form.tags}
          onChange={handleChange}
          placeholder="e.g. auth, payments, ui (comma-separated)"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-1">Separate tags with commas</p>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Feature'}
        </button>
      </div>
    </form>
  );
};

export default FeatureForm;
